import React, { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../components/app-header';
import { AppSideNav } from '../components/app-side-nav';
import { AppFooter } from '../components/app-footer';
import { EventNotificationService, useEventNotification } from '../framework/services/event-notification';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSinglePageAppStyles } from './app-styles';
import { mergeStyles } from '@fluentui/react';
import { AppMenuEvents } from '../events/app-menu';
import { BwjSemiFlexContainer } from '../framework/components/bwj-semi-flex-container';
import { BwjScrollContainer } from '../framework/components/bwj-scroll-container';
import { BwjViewManager, BwjViewState } from '../framework/components/bwj-view-manager';
import { BwjButtonType } from '../framework/components/bwj-button';
import { StartUp } from '../components/startup';
import { BwjEventArgs } from '../framework/entities/bwj-event';
import { AuthenticationEvents } from '../events/authentication';
import { CurrentUserService } from './current-user';
import { BwjToaster } from '../framework/components/bwj-toaster';
/*@begin-app-target*/import { AppPages, AttHubAppType } from '../app-volunteer/app-pages';/*@end-app-target*/

const styles = getSinglePageAppStyles();

const subscriberId = 'Component.AppContent';
const resetMainContentScrollEvent = `${subscriberId}.ResetMainContentScroll`

export const AppContent: React.FunctionComponent = () => {
  const [showViews, setShowViews] = useState(false);
  const [menuAvailable, setMenuAvailable] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [showDesktopMenu, setShowDesktopMenu] = useState(true);
  const [invokeShowMobileMenu, setInvokeShowMobileMenu] = useState<{} | undefined>(undefined);
  const [scrollResetInvocation, setScrollResetInvocation] = useState<BwjEventArgs<null> | undefined>(undefined);
  
  useEventNotification(() => setMenuAvailable(true),[], AppMenuEvents.MenuAvailable, subscriberId);
  useEventNotification(() => setMenuAvailable(false),[], AppMenuEvents.MenuUnavailable, subscriberId);
  useEventNotification(() => setShowMenu(!showMenu), [showMenu], AppMenuEvents.ToggleMenu, subscriberId);
  useEventNotification(() => {
    setViewState(BwjViewState.Loading);
    CurrentUserService.signOut(false)
    .then(() => {
      if((AttHubAppType as any) === 'oversight') {
        window.location.href = 'https://attendanthub.org/sign-in';
      } else {
        window.location.reload();
      }
    });
  }, [], AuthenticationEvents.ForceSignOut, subscriberId);
  useEventNotification(() => setScrollResetInvocation(new BwjEventArgs(null)), [], resetMainContentScrollEvent, subscriberId);
  
  useEffect(() => {
    // only ever do this once.  We do this to ensure that no app view can fire a Menu Available / Unavailable event
    // before this parent component has subscribed to it
    if(!showViews) {
      window.setTimeout(() => {
        setShowViews(true);
        setViewState(BwjViewState.Ready);
      }, 100);
    }
  });

  const appBodyClass = mergeStyles([styles.root]);
  // const mainSectionClass = mergeStyles([styles.main]);
  const contentAreaClass = mergeStyles([styles.contentArea]);

  const onRestartApp = () => {
    window.location.reload();
  }

  const onSignInFailure = useCallback(() => {
    if((AttHubAppType as any) === 'oversight') {
        window.location.href = 'https://attendanthub.org/sign-in';
      }
  }, []);

  return (
    <StartUp onSignInFailure={onSignInFailure}>
      <Router>
        <div className={appBodyClass}>
          <AppHeader
            title={`AttendantHub - ${AttHubAppType}`}
            showMenuButton={menuAvailable}
            onDesktopMenuButtonClick={() => setShowDesktopMenu(!showDesktopMenu)}
            onMobileMenuButtonClick={() => setInvokeShowMobileMenu({})}
          />
          <BwjSemiFlexContainer
            styles={{ root: { flexGrow: 1 }, content: { display: 'flex', flexDirection: 'row', alignItems: 'stretch' } }}
            pinWidth={false}
            pinHeight={true}
          >
            {menuAvailable &&
            <AppSideNav
              title={'App Title'}
              invokeShowMobileNav={invokeShowMobileMenu}
              showDesktopNav={showDesktopMenu}
            />}
            <section className={contentAreaClass}>
              <BwjViewManager
                state={viewState}
                loadingState={{
                  message: 'Loading application...',
                }}
                errorState={{
                  icon: 'StatusErrorFull',
                  message: 'We could not start the app due to an error - try restarting the app.  Please be sure to check your Internet connection',
                  buttons: [{
                    buttonStyle: BwjButtonType.PrimaryStandard,
                    text: 'Restart app',
                    onClick: onRestartApp,
                  }],
                }}
              >
                <BwjScrollContainer
                  containerStyle={{ display: 'flex', flexDirection: 'column' }}
                  invokeScrollReset={scrollResetInvocation}
                >
                  <AppPages />
                </BwjScrollContainer>
              </BwjViewManager>
            </section>
          </BwjSemiFlexContainer>
          <AppFooter />
        </div>
        <BwjToaster
          ariaShowFullMessageLabel={'Display full notification'}
          ariaDismissLabel={'Dismiss notification'}
          closeDialogButtonText={'Close'}
        />
      </Router>
    </StartUp>
  );
};

export const useMainContentScrollReset = () => {
  const fn = () => EventNotificationService.dispatch(resetMainContentScrollEvent);
  return fn;
}
